import React, { ChangeEvent, useState, useEffect, useMemo } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Select, Button } from '@chakra-ui/react';
import Card from "components/card";
import InputField from "components/fields/InputField";
import axios from 'axios';
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";

const math = require('mathjs');

interface Props {
    isOpen: boolean;
    onClose(): void;
    tradeDetail: any;
    setSwalProps?: any;
    isReissue?: boolean;
    previousOfferId?: number;
    previousOfferStatus?: string;
}

interface OfferData {
    costRate: string;
    margin: string;
    expiryHours: string;
    expiryMinutes: string;
    expirySeconds: string;
}

// Enhanced validation utilities with mathjs precision
export const ValidationUtils = {
    isValidNumber: (value: string | number): boolean => {
        if (typeof value === 'string' && value.trim() === '') return false;
        try {
            const num = math.bignumber(value);
            // Use mathjs to check if it's a valid numeric value
            return math.isNumeric(num);
        } catch {
            return false;
        }
    },

    isPositiveNumber: (value: string | number): boolean => {
        try {
            if (!ValidationUtils.isValidNumber(value)) return false;
            const num = math.bignumber(value);
            return math.larger(num, math.bignumber(0));
        } catch {
            return false;
        }
    },

    isNonNegativeNumber: (value: string | number): boolean => {
        try {
            if (!ValidationUtils.isValidNumber(value)) return false;
            const num = math.bignumber(value);
            return math.largerEq(num, math.bignumber(0));
        } catch {
            return false;
        }
    },

    // Safe BigNumber parsing with fallback
    parseNumberSafe: (value: string | number, fallback: number = 0): any => {
        try {
            if (typeof value === 'string' && value.trim() === '') return math.bignumber(fallback);
            const parsed = math.bignumber(value);
            // Only return parsed value if it's valid, otherwise fallback
            return math.isNumeric(parsed) ? parsed : math.bignumber(fallback);
        } catch {
            return math.bignumber(fallback);
        }
    },

    // Convert BigNumber to display string with proper decimal places
    formatWithDecimals: (value: any, decimals: number): string => {
        try {
            return math.format(value, { notation: "fixed", precision: decimals });
        } catch {
            return '0';
        }
    },

    // Input sanitization for numeric fields
    sanitizeNumericInput: (value: string): string => {
        if (value === '') return '';
        if (!/^\d*\.?\d*$/.test(value)) return value.slice(0, -1);
        return value;
    }
};

interface FormValidationResult {
    isValid: boolean;
    errors: Record<string, string>;
}

// Comprehensive form validation with mathjs BigNumber precision
const validateOfferForm = (
    offerData: OfferData,
    totalExpirySeconds: number,
    tradeAmount?: number,
    fromAssetDecimals?: number,
    toAssetDecimals?: number
): FormValidationResult => {
    const errors: Record<string, string> = {};

    // Cost rate validation with mathjs BigNumber precision
    if (!ValidationUtils.isValidNumber(offerData.costRate)) {
        errors.costRate = 'Cost rate must be a valid number';
    } else if (!ValidationUtils.isPositiveNumber(offerData.costRate)) {
        errors.costRate = 'Cost rate must be greater than 0';
    } else {
        try {
            const costRateValue = ValidationUtils.parseNumberSafe(offerData.costRate);
            const maxDecimals = Math.max(fromAssetDecimals || 8, toAssetDecimals || 8);
            const maxRate = math.bignumber(1000000);
            const minRate = math.bignumber(Math.pow(10, -maxDecimals));

            if (math.smallerEq(costRateValue, math.bignumber(0))) {
                errors.costRate = 'Cost rate must be greater than 0';
            } else if (math.larger(costRateValue, maxRate)) {
                errors.costRate = 'Cost rate seems unusually high (> 1,000,000)';
            } else if (math.smaller(costRateValue, minRate)) {
                errors.costRate = `Cost rate seems unusually low (< ${Math.pow(10, -maxDecimals)})`;
            }
        } catch {
            errors.costRate = 'Invalid cost rate format';
        }
    }

    // Margin validation with mathjs BigNumber precision
    if (!offerData.margin || offerData.margin.trim() === '') {
        errors.margin = 'Margin is required';
    } else if (!ValidationUtils.isValidNumber(offerData.margin)) {
        errors.margin = 'Margin must be a valid number';
    } else if (!ValidationUtils.isNonNegativeNumber(offerData.margin)) {
        errors.margin = 'Margin cannot be negative';
    } else {
        try {
            const marginValue = ValidationUtils.parseNumberSafe(offerData.margin);
            const maxMargin = math.bignumber(100);
            const warningMargin = math.bignumber(50);

            if (math.smaller(marginValue, math.bignumber(0))) {
                errors.margin = 'Margin cannot be negative';
            } else if (math.larger(marginValue, maxMargin)) {
                errors.margin = 'Margin cannot exceed 100%';
            } else if (math.larger(marginValue, warningMargin)) {
                errors.margin = 'Margin seems unusually high (> 50%)';
            }
        } catch {
            errors.margin = 'Invalid margin format';
        }
    }

    // Enhanced expiry time validation using mathjs
    const hours = math.number(ValidationUtils.parseNumberSafe(offerData.expiryHours));
    const minutes = math.number(ValidationUtils.parseNumberSafe(offerData.expiryMinutes));
    const seconds = math.number(ValidationUtils.parseNumberSafe(offerData.expirySeconds));

    if (math.smaller(hours, 0) || math.smaller(minutes, 0) || math.smaller(seconds, 0)) {
        errors.expiry = 'Expiry time values cannot be negative';
    } else if (math.larger(hours, 24)) {
        errors.expiry = 'Hours cannot exceed 24';
    } else if (math.largerEq(minutes, 60)) {
        errors.expiry = 'Minutes must be less than 60';
    } else if (math.largerEq(seconds, 60)) {
        errors.expiry = 'Seconds must be less than 60';
    } else if (math.smallerEq(totalExpirySeconds, 0)) {
        errors.expiry = 'Expiry time must be greater than 0';
    } else if (math.smaller(totalExpirySeconds, 60)) {
        errors.expiry = 'Expiry time must be at least 1 minute';
    } else if (math.larger(totalExpirySeconds, 86400)) {
        errors.expiry = 'Expiry time cannot exceed 24 hours';
    }

    // Cross-field validation with mathjs BigNumber precision
    try {
        if (ValidationUtils.isPositiveNumber(offerData.costRate) && ValidationUtils.isNonNegativeNumber(offerData.margin)) {
            const costRate = ValidationUtils.parseNumberSafe(offerData.costRate);
            const margin = ValidationUtils.parseNumberSafe(offerData.margin);

            const marginMultiplier = math.add(math.bignumber(1), math.divide(margin, math.bignumber(100)));
            const finalRate = math.multiply(costRate, marginMultiplier);

            // Check for calculation overflow or invalid results using mathjs
            const finalRateNum = math.number(finalRate);
            if (!isFinite(finalRateNum) || math.smallerEq(finalRate, math.bignumber(0))) {
                errors.calculation = 'Rate calculation resulted in invalid value';
            }

            // Validate final amount calculation with mathjs precision
            if (tradeAmount && ValidationUtils.isPositiveNumber(tradeAmount)) {
                const tradeAmountBN = ValidationUtils.parseNumberSafe(tradeAmount);
                const finalAmount = math.divide(tradeAmountBN, finalRate);

                const finalAmountNum = math.number(finalAmount);
                if (!isFinite(finalAmountNum) || math.smallerEq(finalAmount, math.bignumber(0))) {
                    errors.calculation = 'Final amount calculation is invalid';
                } else {
                    const minAmount = math.bignumber('0.00000001');
                    if (math.smaller(finalAmount, minAmount)) {
                        errors.calculation = 'Final amount is too small to be processed';
                    }
                }
            }
        }
    } catch (calcError) {
        errors.calculation = 'Calculation error occurred';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

const SubmitOfferModal: React.FC<Props> = ({
    isOpen,
    onClose,
    tradeDetail,
    setSwalProps,
    isReissue = false,
    previousOfferId,
    previousOfferStatus
}) => {
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [offerData, setOfferData] = useState<OfferData>({
        costRate: "",
        margin: "0.00",
        expiryHours: "0",
        expiryMinutes: "15",
        expirySeconds: "0",
    });

    const [calculatedRate, setCalculatedRate] = useState<any>(math.bignumber(0));
    const [calculatedAmount, setCalculatedAmount] = useState<any>(math.bignumber(0));
    const [originalCalculatedAmount, setOriginalCalculatedAmount] = useState<any>(math.bignumber(0));
    const [totalExpirySeconds, setTotalExpirySeconds] = useState<number>(900);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const fromAssetDecimals = tradeDetail.from_asset_decimal;
    const toAssetDecimals = tradeDetail.to_asset_decimal;
    const maxCostRateDecimals = fromAssetDecimals || 8;

    const validateDecimalPlaces = (value: string, maxDecimals: number): boolean => {
        if (!value || !value.includes('.')) return true;
        const decimalPart = value.split('.')[1];
        return decimalPart ? decimalPart.length <= maxDecimals : true;
    };

    const handleCostRateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow navigation and editing keys
        if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }

        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
            return;
        }

        // Allow only digits and decimal point
        if (!/[0-9.]/.test(e.key)) {
            e.preventDefault();
            return;
        }

        // Prevent multiple decimal points
        if (e.key === '.' && offerData.costRate.includes('.')) {
            e.preventDefault();
            return;
        }

        // Check decimal places limit when typing after decimal point
        if (offerData.costRate.includes('.')) {
            const currentValue = offerData.costRate;
            const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0;
            const decimalIndex = currentValue.indexOf('.');

            // If typing after decimal point and would exceed max decimals
            if (cursorPosition > decimalIndex && (currentValue.length - decimalIndex - 1) >= maxCostRateDecimals) {
                // Only allow if we're replacing/selecting text
                const selectionLength = ((e.target as HTMLInputElement).selectionEnd || 0) - cursorPosition;
                if (selectionLength === 0) {
                    e.preventDefault();
                }
            }
        }
    };

    // Alternative solution: Add dependency to force validation recalculation
    // You could also modify the validation useMemo to depend on modal state:
    const validation = useMemo(() => {
        // Only validate if modal is open to avoid stale state issues
        if (!isOpen) return { isValid: false, errors: {} };
        return validateOfferForm(offerData, totalExpirySeconds, tradeDetail?.from_amount, tradeDetail?.from_asset_decimal, tradeDetail?.to_asset_decimal);
    }, [offerData, totalExpirySeconds, tradeDetail?.from_amount, isOpen]);

    const isFormValid = validation.isValid;

    useEffect(() => {
        setValidationErrors(validation.errors);
    }, [validation]);

    // Enhanced calculation with mathjs BigNumber precision and decimal handling
    useEffect(() => {
        try {
            const costRateSafe = ValidationUtils.parseNumberSafe(offerData.costRate);
            const marginSafe = ValidationUtils.parseNumberSafe(offerData.margin);
            const fromAmountSafe = ValidationUtils.parseNumberSafe(tradeDetail?.from_amount);

            if (ValidationUtils.isPositiveNumber(offerData.costRate) &&
                ValidationUtils.isNonNegativeNumber(offerData.margin) &&
                ValidationUtils.isPositiveNumber(tradeDetail?.from_amount)) {

                // Calculate rate with margin using mathjs: rate = costRate * (1 + margin/100)
                const marginDivided = math.divide(marginSafe, math.bignumber(100));
                const marginMultiplier = math.add(math.bignumber(1), marginDivided);
                const rate = math.multiply(costRateSafe, marginMultiplier);

                const rateNum = math.number(rate);
                if (isFinite(rateNum) && math.larger(rate, math.bignumber(0))) {
                    setCalculatedRate(rate);

                    // Calculate amounts with mathjs precision
                    const calculatedBtc = math.divide(fromAmountSafe, rate);
                    const originalBtc = math.divide(fromAmountSafe, costRateSafe);

                    const calculatedBtcNum = math.number(calculatedBtc);
                    const originalBtcNum = math.number(originalBtc);

                    if (isFinite(calculatedBtcNum) && isFinite(originalBtcNum)) {
                        setCalculatedAmount(calculatedBtc);
                        setOriginalCalculatedAmount(originalBtc);
                    } else {
                        setCalculatedAmount(math.bignumber(0));
                        setOriginalCalculatedAmount(math.bignumber(0));
                    }
                } else {
                    setCalculatedRate(math.bignumber(0));
                    setCalculatedAmount(math.bignumber(0));
                    setOriginalCalculatedAmount(math.bignumber(0));
                }
            } else {
                setCalculatedRate(math.bignumber(0));
                setCalculatedAmount(math.bignumber(0));
                setOriginalCalculatedAmount(math.bignumber(0));
            }
        } catch (error) {
            console.error('Calculation error:', error);
            setCalculatedRate(math.bignumber(0));
            setCalculatedAmount(math.bignumber(0));
            setOriginalCalculatedAmount(math.bignumber(0));
        }
    }, [offerData.costRate, offerData.margin, tradeDetail?.from_amount]);

    // Enhanced expiry seconds calculation using mathjs
    useEffect(() => {
        const hoursBN = ValidationUtils.parseNumberSafe(offerData.expiryHours);
        const minutesBN = ValidationUtils.parseNumberSafe(offerData.expiryMinutes);
        const secondsBN = ValidationUtils.parseNumberSafe(offerData.expirySeconds);

        // Use mathjs for all time calculations
        const hoursInSeconds = math.multiply(hoursBN, math.bignumber(3600));
        const minutesInSeconds = math.multiply(minutesBN, math.bignumber(60));
        const totalSecondsCalc = math.add(math.add(hoursInSeconds, minutesInSeconds), secondsBN);

        // Convert to regular number for state, but ensure bounds using mathjs
        const totalSecondsNum = math.number(totalSecondsCalc);
        const boundedSeconds = math.max(0, math.min(86400, totalSecondsNum));
        setTotalExpirySeconds(boundedSeconds);
    }, [offerData.expiryHours, offerData.expiryMinutes, offerData.expirySeconds]);

    useEffect(() => {
        if (isOpen) {
            setIsConfirmed(false);
        }
    }, [isOpen]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        let sanitizedValue = value;

        if (name === 'costRate' || name === 'margin') {
            sanitizedValue = ValidationUtils.sanitizeNumericInput(value);

            // Validate decimal places for costRate
            if (name === 'costRate' && !validateDecimalPlaces(sanitizedValue, maxCostRateDecimals)) {
                return; // Don't update state if validation fails
            }
        }

        setOfferData(prev => ({
            ...prev,
            [name]: sanitizedValue,
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleReview = () => {
        if (isFormValid) {
            setIsReviewMode(true);
        }
    };

    const formatExpiryTimeForMessage = () => {
        const hours = math.number(ValidationUtils.parseNumberSafe(offerData.expiryHours));
        const minutes = math.number(ValidationUtils.parseNumberSafe(offerData.expiryMinutes));
        const seconds = math.number(ValidationUtils.parseNumberSafe(offerData.expirySeconds));

        return `${hours}h:${minutes}m:${seconds}s`;
    };

    const calculateExpiryTimestamp = () => {
        const currentTime = Date.now();
        const expiryInMilliseconds = math.multiply(totalExpirySeconds, 1000);
        return math.add(currentTime, expiryInMilliseconds);
    };

    const formatExpiryTime = () => {
        const hours = math.number(ValidationUtils.parseNumberSafe(offerData.expiryHours));
        const minutes = math.number(ValidationUtils.parseNumberSafe(offerData.expiryMinutes));
        const seconds = math.number(ValidationUtils.parseNumberSafe(offerData.expirySeconds));

        const parts = [];
        if (math.larger(hours, 0)) parts.push(`${hours}h`);
        if (math.larger(minutes, 0)) parts.push(`${minutes}m`);
        if (math.larger(seconds, 0)) parts.push(`${seconds}s`);

        return parts.join(' ') || '0s';
    };

    const formatAmountForApi = (value: number, decimals: number) => {
        return math.round(value, decimals);
    };

    const handleSubmitOffer = async () => {
        if (!isFormValid) {
            console.error('Form validation failed:', validation.errors);
            return;
        }

        const runtimeValidation = validateOfferForm(offerData, totalExpirySeconds, tradeDetail?.from_amount, tradeDetail?.from_asset_decimal, tradeDetail?.to_asset_decimal);
        if (!runtimeValidation.isValid) {
            setValidationErrors(runtimeValidation.errors);
            return;
        }

        setIsProcessing(true);

        try {
            // Convert BigNumber values to regular numbers for API submission using mathjs
            const costRateNumber = math.number(ValidationUtils.parseNumberSafe(offerData.costRate));
            const marginNumber = math.number(ValidationUtils.parseNumberSafe(offerData.margin));
            const calculatedRateNumber = math.number(calculatedRate);
            const calculatedAmountNumber = math.number(calculatedAmount);
            const originalCalculatedAmountNumber = math.number(originalCalculatedAmount);

            let requestData: any = {
                trade_id: tradeDetail.id,
                trade_shortid: tradeDetail.shortid,
                ticket_id: tradeDetail.ticket_id,
                data: {
                    offerDetails: {
                        trade_shortid: tradeDetail?.shortid,
                        sell: formatAmountForApi(tradeDetail?.from_amount, fromAssetDecimals),
                        sell_asset: tradeDetail?.from_asset,
                        receive: formatAmountForApi(calculatedAmountNumber, toAssetDecimals),
                        receive_asset: tradeDetail?.to_asset,
                        rate: formatAmountForApi(calculatedRateNumber, fromAssetDecimals),
                        original_rate: formatAmountForApi(costRateNumber, fromAssetDecimals),
                        original_receive: formatAmountForApi(originalCalculatedAmountNumber, toAssetDecimals),
                        settlement_address: tradeDetail?.to_address?.address,
                        status: "pending",
                        expired_text: formatExpiryTimeForMessage(),
                        expired: math.number(calculateExpiryTimestamp())
                    }
                },
                cost_rate: formatAmountForApi(costRateNumber, fromAssetDecimals),
                margin: marginNumber,
                rate: formatAmountForApi(calculatedRateNumber, fromAssetDecimals),
                calculated_amount: formatAmountForApi(calculatedAmountNumber, toAssetDecimals),
                expiry_seconds: totalExpirySeconds,
                is_reissue: isReissue,
                ...(isReissue && { previous_offer_id: previousOfferId })
            };

            await axios
                .post(process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole").toLowerCase()}/trade/action/trade-submit-offer`, requestData)
                .then(function (response) {
                    if (response && response.data?.status === "ok") {
                        closeModal();

                        if (setSwalProps) {
                            setSwalProps({
                                show: true,
                                icon: 'success',
                                title: 'Success',
                                html: `Your ${isReissue ? 'reissue has' : 'offer has'} been submitted successfully`,
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                },
                            });
                        }
                    } else {
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: response.data?.message || `An error occurred while ${isReissue ? 'reissuing' : 'submitting'} your offer.`,
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    }
                })
                .catch(function (error) {
                    let errorMessage = `Offer ${isReissue ? 'reissue' : 'submission'} has failed`;
                    if (error.response && (error.response.status === 400 || error.response.status === 500)) {
                        errorMessage = error.response.data?.message || errorMessage;
                    }

                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: errorMessage,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
        }
    };

    const closeModal = async () => {
        setOfferData({
            costRate: "",
            margin: "0.00",
            expiryHours: "0",
            expiryMinutes: "15",
            expirySeconds: "0",
        });
        setCalculatedRate(math.bignumber(0));
        setCalculatedAmount(math.bignumber(0));
        setOriginalCalculatedAmount(math.bignumber(0));
        setTotalExpirySeconds(900);
        setValidationErrors({});
        setIsReviewMode(false);
        setIsProcessing(false);
        setIsConfirmed(false);
        onClose();
    };

    const handleBack = () => {
        setIsReviewMode(false);
        setIsConfirmed(false);
    };

    // Generate options for dropdowns using mathjs for range calculations
    const hourOptions = Array.from({ length: math.add(24, 1) }, (_, i) => i); // 0-24 hours
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i); // 0-59 minutes
    const secondOptions = Array.from({ length: 60 }, (_, i) => i); // 0-59 seconds

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeModal}
            closeOnOverlayClick={false}
        >
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto w-full max-w-[500px] px-4 md:top-[12vh]">
                <ModalBody className="p-0 !text-navy-700">
                    <Card extra="px-6 pt-8 pb-10 flex flex-col !z-[1004]">
                        {!isReviewMode ? (
                            <>
                                <h1 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
                                    {isReissue ? 'Reissue Offer' : 'Submit Offer'}
                                </h1>

                                {validationErrors.calculation && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                        <p className="text-sm text-red-600">{validationErrors.calculation}</p>
                                    </div>
                                )}

                                <div className="space-y-5">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-navy-700 dark:text-white">
                                                From
                                            </label>
                                            <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                                {tradeDetail?.from_asset || 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm text-navy-700 dark:text-white">
                                                To
                                            </label>
                                            <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                                {tradeDetail?.to_asset || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm text-navy-700 dark:text-white">
                                            Amount
                                        </label>
                                        <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                            <DisplayAmountWithDecimals amount={Number(tradeDetail?.from_amount || 0)} currency={tradeDetail?.from_asset || ''} decimal={tradeDetail?.from_asset_decimal}/>
                                        </div>
                                    </div>
                                    <div>
                                        <InputField
                                            disabled={isProcessing}
                                            variant="auth"
                                            extra="shadow-sm"
                                            label="Cost Rate"
                                            required={true}
                                            id="costRate"
                                            name="costRate"
                                            type="text"
                                            placeholder="Enter cost rate"
                                            value={offerData.costRate}
                                            onKeyDown={handleCostRateKeyDown}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                        {validationErrors.costRate && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.costRate}</p>
                                        )}
                                    </div>
                                    <div>
                                        <InputField
                                            disabled={isProcessing}
                                            variant="auth"
                                            extra="shadow-sm"
                                            label="Margin (%)"
                                            required={true}
                                            id="margin"
                                            name="margin"
                                            type="text"
                                            placeholder="Enter margin percentage"
                                            value={offerData.margin}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Margin percentage (e.g. 0.25 for 0.25%)
                                        </p>
                                        {validationErrors.margin && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.margin}</p>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-navy-700 dark:text-white">
                                                Rate
                                            </label>
                                            <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                                {math.larger(calculatedRate, math.bignumber(0)) ?
                                                    ValidationUtils.formatWithDecimals(calculatedRate, fromAssetDecimals) : '-'
                                                }
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm text-navy-700 dark:text-white">
                                                Amount
                                            </label>
                                            <div className="text-sm font-semibold text-navy-700 dark:text-white">
                                                {math.larger(calculatedAmount, math.bignumber(0)) ?
                                                    `${ValidationUtils.formatWithDecimals(calculatedAmount, toAssetDecimals)} ${tradeDetail?.to_asset || ''}` :
                                                    '-'
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-navy-700 dark:text-white block mb-2">
                                            Offer Expiry
                                        </label>
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                                    Hours
                                                </label>
                                                <Select
                                                    disabled={isProcessing}
                                                    name="expiryHours"
                                                    value={offerData.expiryHours}
                                                    onChange={handleInputChange}
                                                    className="w-full h-7 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none text-sm pl-3 shadow-sm"
                                                    size="sm"
                                                >
                                                    {hourOptions.map(hour => (
                                                        <option key={hour} value={hour.toString()}>
                                                            {hour}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                                    Minutes
                                                </label>
                                                <Select
                                                    disabled={isProcessing}
                                                    name="expiryMinutes"
                                                    value={offerData.expiryMinutes}
                                                    onChange={handleInputChange}
                                                    className="w-full h-7 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none text-sm pl-3 shadow-sm"
                                                    size="sm"
                                                >
                                                    {minuteOptions.map(minute => (
                                                        <option key={minute} value={minute.toString()}>
                                                            {minute}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                                    Seconds
                                                </label>
                                                <Select
                                                    disabled={isProcessing}
                                                    name="expirySeconds"
                                                    value={offerData.expirySeconds}
                                                    onChange={handleInputChange}
                                                    className="w-full h-7 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none text-sm pl-3 shadow-sm"
                                                    size="sm"
                                                >
                                                    {secondOptions.map(second => (
                                                        <option key={second} value={second.toString()}>
                                                            {second}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </div>
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                            Total expiry time: {formatExpiryTime()}
                                        </p>
                                        {validationErrors.expiry && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.expiry}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-3 pt-1">
                                        <Button
                                            onClick={closeModal}
                                            isDisabled={isProcessing}
                                            className="btn btn-default w-1/2"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleReview}
                                            isDisabled={!isFormValid || isProcessing}
                                            className="btn btn-primary w-1/2"
                                        >
                                            Review
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h1 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
                                    Review {isReissue ? 'Reissue' : ''} Offer
                                </h1>
                                {isReissue && previousOfferStatus === "pending" && (
                                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                        <p className="text-sm text-yellow-700">
                                            Warning: Reissuing this offer will cancel your previous offer.
                                        </p>
                                    </div>
                                )}
                                <div className="space-y-4">
                                    <div className="rounded-lg border border-gray-200 dark:border-white/10 p-4">
                                        <h3 className="mb-3 font-semibold text-navy-700 dark:text-white">
                                            Offer Details
                                        </h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">From:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {tradeDetail?.from_asset || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">To:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {tradeDetail?.to_asset || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Amount:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    <DisplayAmountWithDecimals amount={Number(tradeDetail?.from_amount || 0)} currency={tradeDetail?.from_asset || ''} decimal={tradeDetail?.from_asset_decimal} />
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Cost Rate:</span>
                                                <DisplayAmountWithDecimals
                                                    amount={Number(
                                                        math.round(
                                                            ValidationUtils.parseNumberSafe(offerData.costRate),
                                                            fromAssetDecimals
                                                        )
                                                    )}
                                                    currency={tradeDetail?.from_asset || ''}
                                                    decimal={tradeDetail?.from_asset_decimal}
                                                    className="text-sm font-semibold text-navy-700 dark:text-white"
                                                />
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Margin:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {offerData.margin}%
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Final Rate:</span>
                                                <DisplayAmountWithDecimals
                                                    amount={Number(
                                                        math.round(
                                                            ValidationUtils.parseNumberSafe(calculatedRate),
                                                            fromAssetDecimals
                                                        )
                                                    )}
                                                    currency={tradeDetail?.from_asset || ''}
                                                    decimal={tradeDetail?.from_asset_decimal}
                                                    className="text-sm font-semibold text-navy-700 dark:text-white"
                                                />
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Final Amount:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {ValidationUtils.formatWithDecimals(calculatedAmount, toAssetDecimals)} {tradeDetail?.to_asset || ''}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Expires In:</span>
                                                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {formatExpiryTime()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-white/10 p-4">
                                        <input
                                            type="checkbox"
                                            id="confirmReview"
                                            checked={isConfirmed}
                                            onChange={(e) => setIsConfirmed(e.target.checked)}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <label htmlFor="confirmReview" className="text-sm text-navy-700 dark:text-white cursor-pointer">
                                            I have carefully reviewed all the offer details above and confirm that the information is correct. I understand that this action cannot be undone once submitted.
                                        </label>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        onClick={handleBack}
                                        isDisabled={isProcessing}
                                        className="btn btn-default w-1/2"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleSubmitOffer}
                                        isDisabled={isProcessing || !isConfirmed}
                                        isLoading={isProcessing}
                                        loadingText="Processing..."
                                        className="btn btn-primary w-1/2"
                                    >
                                        {isReissue ? 'Reissue' : 'Submit'} Offer
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SubmitOfferModal;